/**
 * 租户控制台演示数据场景
 * 
 * 这是演示模式的唯一数据源，所有演示数据从这里派生，确保数据一致性。
 */

import type {
  TenantUser,
  TeamMember,
  AuditLogItem,
  DashboardStats,
  DashboardUserGrowthItem,
  DashboardRevenueItem,
  TenantOrderRecord,
} from "@/types/tenantConsole";

/**
 * 演示用户列表（50个用户：5个教师 + 45个学生）
 */
export const demoUsers: TenantUser[] = [
  // ========== 教师 (5 users, role: 3) ==========
  {
    id: 1001,
    username: "张雨婷",
    phone: "138****2847",
    role: 3,
    boundAt: "2026-01-15 09:30:00",
    activePlans: [{ id: 1, name: "IELTS 全科套餐" }],
    activeSubscription: {
      id: 101,
      status: "ACTIVE",
      startDate: "2026-01-15",
      endDate: "2027-01-15",
      actualAmount: 299900,
    },
  },
  {
    id: 1002,
    username: "David Chen",
    phone: "139****6193",
    role: 3,
    boundAt: "2026-01-20 14:20:00",
    activePlans: [{ id: 2, name: "口语专项" }],
    activeSubscription: {
      id: 102,
      status: "ACTIVE",
      startDate: "2026-01-20",
      endDate: "2026-07-20",
      actualAmount: 99900,
    },
  },
  {
    id: 1003,
    username: "王晓明",
    phone: "186****4521",
    role: 3,
    boundAt: "2026-01-10 10:15:00",
    activePlans: [],
    activeSubscription: null,
  },
  {
    id: 1004,
    username: "Michael Zhang",
    phone: "188****7309",
    role: 3,
    boundAt: "2026-02-01 11:45:00",
    activePlans: [{ id: 1, name: "IELTS 全科套餐" }],
    activeSubscription: {
      id: 103,
      status: "ACTIVE",
      startDate: "2026-02-01",
      endDate: "2027-02-01",
      actualAmount: 299900,
    },
  },
  {
    id: 1005,
    username: "李娜",
    phone: "159****8164",
    role: 3,
    boundAt: "2025-12-20 16:30:00",
    activePlans: [{ id: 3, name: "听力专项" }],
    activeSubscription: {
      id: 104,
      status: "EXPIRED",
      startDate: "2025-12-20",
      endDate: "2026-02-28",
      actualAmount: 79900,
    },
  },

  // ========== 有效订阅学生 (12 users, role: 0) ==========
  {
    id: 1006,
    username: "刘晨",
    phone: "138****5729",
    role: 0,
    boundAt: "2026-02-10 09:20:00",
    activePlans: [{ id: 1, name: "IELTS 全科套餐" }],
    activeSubscription: {
      id: 105,
      status: "ACTIVE",
      startDate: "2026-02-10",
      endDate: "2027-02-10",
      actualAmount: 299900,
    },
  },
  {
    id: 1007,
    username: "Sophie Zhang",
    phone: "139****3841",
    role: 0,
    boundAt: "2026-02-15 13:45:00",
    activePlans: [{ id: 2, name: "口语专项" }],
    activeSubscription: {
      id: 106,
      status: "ACTIVE",
      startDate: "2026-02-15",
      endDate: "2026-08-15",
      actualAmount: 99900,
    },
  },
  {
    id: 1008,
    username: "陈浩",
    phone: "186****9205",
    role: 0,
    boundAt: "2026-02-18 10:30:00",
    activePlans: [{ id: 1, name: "IELTS 全科套餐" }],
    activeSubscription: {
      id: 107,
      status: "ACTIVE",
      startDate: "2026-02-18",
      endDate: "2027-02-18",
      actualAmount: 299900,
    },
  },
  {
    id: 1009,
    username: "Jessica Wang",
    phone: "188****1647",
    role: 0,
    boundAt: "2026-02-22 15:10:00",
    activePlans: [{ id: 3, name: "听力专项" }],
    activeSubscription: {
      id: 108,
      status: "ACTIVE",
      startDate: "2026-02-22",
      endDate: "2026-08-22",
      actualAmount: 79900,
    },
  },
  {
    id: 1010,
    username: "吴凯文",
    phone: "159****4382",
    role: 0,
    boundAt: "2026-02-25 11:25:00",
    activePlans: [{ id: 1, name: "IELTS 全科套餐" }],
    activeSubscription: {
      id: 109,
      status: "ACTIVE",
      startDate: "2026-02-25",
      endDate: "2027-02-25",
      actualAmount: 299900,
    },
  },
  {
    id: 1011,
    username: "Amy Lin",
    phone: "176****7956",
    role: 0,
    boundAt: "2026-03-01 09:40:00",
    activePlans: [{ id: 2, name: "口语专项" }],
    activeSubscription: {
      id: 110,
      status: "ACTIVE",
      startDate: "2026-03-01",
      endDate: "2026-09-01",
      actualAmount: 99900,
    },
  },
  {
    id: 1012,
    username: "赵瑞恩",
    phone: "185****2108",
    role: 0,
    boundAt: "2026-03-03 14:15:00",
    activePlans: [{ id: 1, name: "IELTS 全科套餐" }],
    activeSubscription: {
      id: 111,
      status: "ACTIVE",
      startDate: "2026-03-03",
      endDate: "2027-03-03",
      actualAmount: 299900,
    },
  },
  {
    id: 1013,
    username: "Emily Xu",
    phone: "138****6473",
    role: 0,
    boundAt: "2026-03-05 10:50:00",
    activePlans: [{ id: 1, name: "IELTS 全科套餐" }],
    activeSubscription: {
      id: 112,
      status: "ACTIVE",
      startDate: "2026-03-05",
      endDate: "2027-03-05",
      actualAmount: 299900,
    },
  },
  {
    id: 1014,
    username: "李丹尼尔",
    phone: "139****3590",
    role: 0,
    boundAt: "2026-03-07 16:20:00",
    activePlans: [{ id: 3, name: "听力专项" }],
    activeSubscription: {
      id: 113,
      status: "ACTIVE",
      startDate: "2026-03-07",
      endDate: "2026-09-07",
      actualAmount: 79900,
    },
  },
  {
    id: 1015,
    username: "杨雅琪",
    phone: "186****8214",
    role: 0,
    boundAt: "2026-03-08 11:35:00",
    activePlans: [{ id: 1, name: "IELTS 全科套餐" }],
    activeSubscription: {
      id: 114,
      status: "ACTIVE",
      startDate: "2026-03-08",
      endDate: "2027-03-08",
      actualAmount: 299900,
    },
  },
  {
    id: 1016,
    username: "Jason Huang",
    phone: "188****4761",
    role: 0,
    boundAt: "2026-03-10 13:10:00",
    activePlans: [{ id: 2, name: "口语专项" }],
    activeSubscription: {
      id: 115,
      status: "ACTIVE",
      startDate: "2026-03-10",
      endDate: "2026-09-10",
      actualAmount: 99900,
    },
  },
  {
    id: 1017,
    username: "高欣怡",
    phone: "159****9025",
    role: 0,
    boundAt: "2026-03-11 15:45:00",
    activePlans: [{ id: 1, name: "IELTS 全科套餐" }],
    activeSubscription: {
      id: 116,
      status: "ACTIVE",
      startDate: "2026-03-11",
      endDate: "2027-03-11",
      actualAmount: 299900,
    },
  },

  // ========== 过期订阅学生 (4 users, role: 0) ==========
  {
    id: 1018,
    username: "马晨曦",
    phone: "176****1538",
    role: 0,
    boundAt: "2025-11-15 10:20:00",
    activePlans: [{ id: 1, name: "IELTS 全科套餐" }],
    activeSubscription: {
      id: 117,
      status: "EXPIRED",
      startDate: "2025-11-15",
      endDate: "2026-02-15",
      actualAmount: 299900,
    },
  },
  {
    id: 1019,
    username: "Rachel Sun",
    phone: "185****7294",
    role: 0,
    boundAt: "2025-10-20 14:30:00",
    activePlans: [{ id: 2, name: "口语专项" }],
    activeSubscription: {
      id: 118,
      status: "EXPIRED",
      startDate: "2025-10-20",
      endDate: "2026-01-20",
      actualAmount: 99900,
    },
  },
  {
    id: 1020,
    username: "唐宇轩",
    phone: "138****3867",
    role: 0,
    boundAt: "2025-12-01 09:15:00",
    activePlans: [{ id: 3, name: "听力专项" }],
    activeSubscription: {
      id: 119,
      status: "EXPIRED",
      startDate: "2025-12-01",
      endDate: "2026-02-20",
      actualAmount: 79900,
    },
  },
  {
    id: 1021,
    username: "周娜娜",
    phone: "139****6142",
    role: 0,
    boundAt: "2025-11-10 16:40:00",
    activePlans: [{ id: 1, name: "IELTS 全科套餐" }],
    activeSubscription: {
      id: 120,
      status: "EXPIRED",
      startDate: "2025-11-10",
      endDate: "2026-01-10",
      actualAmount: 299900,
    },
  },

  // ========== 无订阅学生 (29 users, role: 0) ==========
  {
    id: 1022,
    username: "林思远",
    phone: "186****2749",
    role: 0,
    boundAt: "2026-01-12 10:30:00",
    activePlans: [],
    activeSubscription: null,
  },
  {
    id: 1023,
    username: "Kevin Wu",
    phone: "188****5183",
    role: 0,
    boundAt: "2026-01-18 14:20:00",
    activePlans: [],
    activeSubscription: null,
  },
  {
    id: 1024,
    username: "张梦琪",
    phone: "159****8426",
    role: 0,
    boundAt: "2026-01-22 09:45:00",
    activePlans: [],
    activeSubscription: null,
  },
  {
    id: 1025,
    username: "Olivia Chen",
    phone: "176****3691",
    role: 0,
    boundAt: "2026-01-25 11:15:00",
    activePlans: [],
    activeSubscription: null,
  },
  {
    id: 1026,
    username: "王浩然",
    phone: "185****7204",
    role: 0,
    boundAt: "2026-01-28 15:30:00",
    activePlans: [],
    activeSubscription: null,
  },
  {
    id: 1027,
    username: "Sarah Li",
    phone: "138****4957",
    role: 0,
    boundAt: "2026-02-02 10:10:00",
    activePlans: [],
    activeSubscription: null,
  },
  {
    id: 1028,
    username: "黄雨欣",
    phone: "139****6281",
    role: 0,
    boundAt: "2026-02-05 13:25:00",
    activePlans: [],
    activeSubscription: null,
  },
  {
    id: 1029,
    username: "Daniel Zhao",
    phone: "186****1734",
    role: 0,
    boundAt: "2026-02-08 16:40:00",
    activePlans: [],
    activeSubscription: null,
  },
  {
    id: 1030,
    username: "刘佳怡",
    phone: "188****9052",
    role: 0,
    boundAt: "2026-02-11 09:55:00",
    activePlans: [],
    activeSubscription: null,
  },
  {
    id: 1031,
    username: "Alex Liu",
    phone: "159****3468",
    role: 0,
    boundAt: "2026-02-14 14:10:00",
    activePlans: [],
    activeSubscription: null,
  },
  {
    id: 1032,
    username: "陈思涵",
    phone: "176****7825",
    role: 0,
    boundAt: "2026-02-17 11:30:00",
    activePlans: [],
    activeSubscription: null,
  },
  {
    id: 1033,
    username: "Emma Wang",
    phone: "185****2196",
    role: 0,
    boundAt: "2026-02-20 15:45:00",
    activePlans: [],
    activeSubscription: null,
  },
  {
    id: 1034,
    username: "吴宇航",
    phone: "138****5743",
    role: 0,
    boundAt: "2026-02-23 10:20:00",
    activePlans: [],
    activeSubscription: null,
  },
  {
    id: 1035,
    username: "Ryan Zhang",
    phone: "139****8307",
    role: 0,
    boundAt: "2026-02-26 13:35:00",
    activePlans: [],
    activeSubscription: null,
  },
  {
    id: 1036,
    username: "杨诗涵",
    phone: "186****4619",
    role: 0,
    boundAt: "2026-02-28 16:50:00",
    activePlans: [],
    activeSubscription: null,
  },
  {
    id: 1037,
    username: "Grace Yang",
    phone: "188****1952",
    role: 0,
    boundAt: "2026-03-02 09:15:00",
    activePlans: [],
    activeSubscription: null,
  },
  {
    id: 1038,
    username: "赵子轩",
    phone: "159****7284",
    role: 0,
    boundAt: "2026-03-04 12:30:00",
    activePlans: [],
    activeSubscription: null,
  },
  {
    id: 1039,
    username: "Jason Wu",
    phone: "176****3561",
    role: 0,
    boundAt: "2026-03-06 15:45:00",
    activePlans: [],
    activeSubscription: null,
  },
  {
    id: 1040,
    username: "李欣怡",
    phone: "185****9127",
    role: 0,
    boundAt: "2026-03-09 10:00:00",
    activePlans: [],
    activeSubscription: null,
  },
  {
    id: 1041,
    username: "Sophie Liu",
    phone: "138****6483",
    role: 0,
    boundAt: "2026-03-12 13:15:00",
    activePlans: [],
    activeSubscription: null,
  },
  {
    id: 1042,
    username: "王俊杰",
    phone: "139****2750",
    role: 0,
    boundAt: "2026-01-08 11:20:00",
    activePlans: [],
    activeSubscription: null,
  },
  {
    id: 1043,
    username: "Tom Chen",
    phone: "186****5894",
    role: 0,
    boundAt: "2026-01-14 14:35:00",
    activePlans: [],
    activeSubscription: null,
  },
  {
    id: 1044,
    username: "张雨萱",
    phone: "188****3217",
    role: 0,
    boundAt: "2026-01-19 09:50:00",
    activePlans: [],
    activeSubscription: null,
  },
  {
    id: 1045,
    username: "Jessica Li",
    phone: "159****7641",
    role: 0,
    boundAt: "2026-01-24 12:05:00",
    activePlans: [],
    activeSubscription: null,
  },
  {
    id: 1046,
    username: "陈宇轩",
    phone: "176****4928",
    role: 0,
    boundAt: "2026-01-30 15:20:00",
    activePlans: [],
    activeSubscription: null,
  },
  {
    id: 1047,
    username: "Eric Tang",
    phone: "185****1365",
    role: 0,
    boundAt: "2026-02-04 10:35:00",
    activePlans: [],
    activeSubscription: null,
  },
  {
    id: 1048,
    username: "刘雨婷",
    phone: "138****8702",
    role: 0,
    boundAt: "2026-02-09 13:50:00",
    activePlans: [],
    activeSubscription: null,
  },
  {
    id: 1049,
    username: "Nina Zhou",
    phone: "139****5146",
    role: 0,
    boundAt: "2026-02-13 16:05:00",
    activePlans: [],
    activeSubscription: null,
  },
  {
    id: 1050,
    username: "吴思琪",
    phone: "186****9573",
    role: 0,
    boundAt: "2026-02-19 11:40:00",
    activePlans: [],
    activeSubscription: null,
  },
];

/**
 * 演示订单记录。
 */
export const demoOrders: TenantOrderRecord[] = [
  {
    subscriptionId: 926,
    userId: 934,
    username: "陈思涵",
    planName: "IELTS 冲刺包",
    pricingName: "MONTHLY 30天",
    orderAmount: 39900,
    settlementAmount: 24900,
    commissionAmount: 15000,
    paymentStatus: "PAID",
    commissionStatus: "PENDING",
    paymentTime: "2026-03-30T20:18:00+08:00",
    refundedAt: null,
    createdAt: "2026-03-30T20:18:00+08:00",
  },
  {
    subscriptionId: 925,
    userId: 933,
    username: "Jason Wu",
    planName: "写作批改包",
    pricingName: "QUARTERLY 90天",
    orderAmount: 69900,
    settlementAmount: 45900,
    commissionAmount: 24000,
    paymentStatus: "PAID",
    commissionStatus: "PENDING",
    paymentTime: "2026-03-29T14:42:00+08:00",
    refundedAt: null,
    createdAt: "2026-03-29T14:42:00+08:00",
  },
  {
    subscriptionId: 924,
    userId: 931,
    username: "赵雨菲",
    planName: "口语私教包",
    pricingName: "MONTHLY 30天",
    orderAmount: 89900,
    settlementAmount: 59900,
    commissionAmount: 30000,
    paymentStatus: "PAID",
    commissionStatus: "PENDING",
    paymentTime: "2026-03-28T09:26:00+08:00",
    refundedAt: null,
    createdAt: "2026-03-28T09:26:00+08:00",
  },
  {
    subscriptionId: 923,
    userId: 930,
    username: "刘浩然",
    planName: "IELTS 高阶包",
    pricingName: "HALF_YEAR 180天",
    orderAmount: 159900,
    settlementAmount: 99900,
    commissionAmount: 60000,
    paymentStatus: "PAID",
    commissionStatus: "PENDING",
    paymentTime: "2026-03-27T18:10:00+08:00",
    refundedAt: null,
    createdAt: "2026-03-27T18:10:00+08:00",
  },
  {
    subscriptionId: 922,
    userId: 929,
    username: "Mia Lin",
    planName: "IELTS 冲刺包",
    pricingName: "QUARTERLY 90天",
    orderAmount: 89900,
    settlementAmount: 54900,
    commissionAmount: 35000,
    paymentStatus: "PAID",
    commissionStatus: "SETTLED",
    paymentTime: "2026-03-26T11:35:00+08:00",
    refundedAt: null,
    createdAt: "2026-03-26T11:35:00+08:00",
  },
  {
    subscriptionId: 921,
    userId: 928,
    username: "周可欣",
    planName: "写作批改包",
    pricingName: "MONTHLY 30天",
    orderAmount: 29900,
    settlementAmount: 17900,
    commissionAmount: 12000,
    paymentStatus: "PAID",
    commissionStatus: "PENDING",
    paymentTime: "2026-03-25T16:08:00+08:00",
    refundedAt: null,
    createdAt: "2026-03-25T16:08:00+08:00",
  },
  {
    subscriptionId: 920,
    userId: 927,
    username: "林书宇",
    planName: "IELTS 高阶包",
    pricingName: "ANNUAL 365天",
    orderAmount: 269900,
    settlementAmount: 179900,
    commissionAmount: 90000,
    paymentStatus: "PAID",
    commissionStatus: "SETTLED",
    paymentTime: "2026-03-24T10:12:00+08:00",
    refundedAt: null,
    createdAt: "2026-03-24T10:12:00+08:00",
  },
  {
    subscriptionId: 904,
    userId: 912,
    username: "用户 912",
    planName: "IELTS 高阶包",
    pricingName: "MONTHLY 30天",
    orderAmount: 50000,
    settlementAmount: 29900,
    commissionAmount: 20100,
    paymentStatus: "REFUNDED",
    commissionStatus: "REFUNDED",
    paymentTime: "2026-03-23T15:36:00+08:00",
    refundedAt: "2026-03-23T16:20:00+08:00",
    createdAt: "2026-03-23T15:36:00+08:00",
  },
  {
    subscriptionId: 903,
    userId: 911,
    username: "用户 911",
    planName: "IELTS 高阶包",
    pricingName: "MONTHLY 30天",
    orderAmount: 49900,
    settlementAmount: 29900,
    commissionAmount: 20000,
    paymentStatus: "PAID",
    commissionStatus: "PENDING",
    paymentTime: "2026-03-20T11:00:00+08:00",
    refundedAt: null,
    createdAt: "2026-03-20T11:00:00+08:00",
  },
  {
    subscriptionId: 902,
    userId: 910,
    username: "孙悦宁",
    planName: "口语私教包",
    pricingName: "QUARTERLY 90天",
    orderAmount: 129900,
    settlementAmount: 89900,
    commissionAmount: 40000,
    paymentStatus: "PAID",
    commissionStatus: "PENDING",
    paymentTime: "2026-03-19T19:32:00+08:00",
    refundedAt: null,
    createdAt: "2026-03-19T19:32:00+08:00",
  },
  {
    subscriptionId: 901,
    userId: 909,
    username: "Grace He",
    planName: "IELTS 冲刺包",
    pricingName: "MONTHLY 30天",
    orderAmount: 45900,
    settlementAmount: 25900,
    commissionAmount: 20000,
    paymentStatus: "PAID",
    commissionStatus: "SETTLED",
    paymentTime: "2026-03-18T13:24:00+08:00",
    refundedAt: null,
    createdAt: "2026-03-18T13:24:00+08:00",
  },
  {
    subscriptionId: 900,
    userId: 908,
    username: "何嘉怡",
    planName: "写作批改包",
    pricingName: "HALF_YEAR 180天",
    orderAmount: 119900,
    settlementAmount: 74900,
    commissionAmount: 45000,
    paymentStatus: "PAID",
    commissionStatus: "SETTLED",
    paymentTime: "2026-03-16T17:45:00+08:00",
    refundedAt: null,
    createdAt: "2026-03-16T17:45:00+08:00",
  },
  {
    subscriptionId: 899,
    userId: 907,
    username: "郑博文",
    planName: "IELTS 高阶包",
    pricingName: "MONTHLY 30天",
    orderAmount: 49900,
    settlementAmount: 29900,
    commissionAmount: 20000,
    paymentStatus: "REFUNDED",
    commissionStatus: "REFUNDED",
    paymentTime: "2026-03-15T12:06:00+08:00",
    refundedAt: "2026-03-16T09:20:00+08:00",
    createdAt: "2026-03-15T12:06:00+08:00",
  },
  {
    subscriptionId: 898,
    userId: 906,
    username: "唐若溪",
    planName: "口语私教包",
    pricingName: "MONTHLY 30天",
    orderAmount: 69900,
    settlementAmount: 45900,
    commissionAmount: 24000,
    paymentStatus: "PAID",
    commissionStatus: "PENDING",
    paymentTime: "2026-03-14T20:10:00+08:00",
    refundedAt: null,
    createdAt: "2026-03-14T20:10:00+08:00",
  },
  {
    subscriptionId: 897,
    userId: 905,
    username: "Kevin Luo",
    planName: "IELTS 冲刺包",
    pricingName: "HALF_YEAR 180天",
    orderAmount: 149900,
    settlementAmount: 94900,
    commissionAmount: 55000,
    paymentStatus: "PAID",
    commissionStatus: "SETTLED",
    paymentTime: "2026-03-12T08:58:00+08:00",
    refundedAt: null,
    createdAt: "2026-03-12T08:58:00+08:00",
  },
  {
    subscriptionId: 896,
    userId: 904,
    username: "冯静雯",
    planName: "写作批改包",
    pricingName: "MONTHLY 30天",
    orderAmount: 25900,
    settlementAmount: 15900,
    commissionAmount: 10000,
    paymentStatus: "PAID",
    commissionStatus: "PENDING",
    paymentTime: "2026-03-10T15:16:00+08:00",
    refundedAt: null,
    createdAt: "2026-03-10T15:16:00+08:00",
  },
  {
    subscriptionId: 895,
    userId: 903,
    username: "夏子涵",
    planName: "IELTS 高阶包",
    pricingName: "QUARTERLY 90天",
    orderAmount: 109900,
    settlementAmount: 69900,
    commissionAmount: 40000,
    paymentStatus: "PAID",
    commissionStatus: "SETTLED",
    paymentTime: "2026-03-08T11:48:00+08:00",
    refundedAt: null,
    createdAt: "2026-03-08T11:48:00+08:00",
  },
  {
    subscriptionId: 894,
    userId: 902,
    username: "Ethan Guo",
    planName: "口语私教包",
    pricingName: "MONTHLY 30天",
    orderAmount: 75900,
    settlementAmount: 49900,
    commissionAmount: 26000,
    paymentStatus: "REFUNDED",
    commissionStatus: "REFUNDED",
    paymentTime: "2026-03-06T18:35:00+08:00",
    refundedAt: "2026-03-07T10:05:00+08:00",
    createdAt: "2026-03-06T18:35:00+08:00",
  },
  {
    subscriptionId: 893,
    userId: 901,
    username: "蒋依依",
    planName: "IELTS 冲刺包",
    pricingName: "QUARTERLY 90天",
    orderAmount: 79900,
    settlementAmount: 49900,
    commissionAmount: 30000,
    paymentStatus: "PAID",
    commissionStatus: "SETTLED",
    paymentTime: "2026-03-05T09:12:00+08:00",
    refundedAt: null,
    createdAt: "2026-03-05T09:12:00+08:00",
  },
  {
    subscriptionId: 892,
    userId: 900,
    username: "陈景行",
    planName: "IELTS 高阶包",
    pricingName: "ANNUAL 365天",
    orderAmount: 259900,
    settlementAmount: 169900,
    commissionAmount: 90000,
    paymentStatus: "PAID",
    commissionStatus: "SETTLED",
    paymentTime: "2026-03-02T21:04:00+08:00",
    refundedAt: null,
    createdAt: "2026-03-02T21:04:00+08:00",
  },
  {
    subscriptionId: 891,
    userId: 899,
    username: "Luna Qian",
    planName: "写作批改包",
    pricingName: "MONTHLY 30天",
    orderAmount: 32900,
    settlementAmount: 19900,
    commissionAmount: 13000,
    paymentStatus: "PAID",
    commissionStatus: "PENDING",
    paymentTime: "2026-02-28T14:18:00+08:00",
    refundedAt: null,
    createdAt: "2026-02-28T14:18:00+08:00",
  },
  {
    subscriptionId: 890,
    userId: 898,
    username: "顾晨曦",
    planName: "口语私教包",
    pricingName: "HALF_YEAR 180天",
    orderAmount: 169900,
    settlementAmount: 109900,
    commissionAmount: 60000,
    paymentStatus: "PAID",
    commissionStatus: "SETTLED",
    paymentTime: "2026-02-25T12:30:00+08:00",
    refundedAt: null,
    createdAt: "2026-02-25T12:30:00+08:00",
  },
  {
    subscriptionId: 889,
    userId: 897,
    username: "徐婉清",
    planName: "IELTS 高阶包",
    pricingName: "QUARTERLY 90天",
    orderAmount: 99900,
    settlementAmount: 64900,
    commissionAmount: 35000,
    paymentStatus: "PAID",
    commissionStatus: "SETTLED",
    paymentTime: "2026-02-21T16:56:00+08:00",
    refundedAt: null,
    createdAt: "2026-02-21T16:56:00+08:00",
  },
  {
    subscriptionId: 888,
    userId: 896,
    username: "Peter Sun",
    planName: "IELTS 冲刺包",
    pricingName: "MONTHLY 30天",
    orderAmount: 42900,
    settlementAmount: 25900,
    commissionAmount: 17000,
    paymentStatus: "REFUNDED",
    commissionStatus: "REFUNDED",
    paymentTime: "2026-02-18T10:14:00+08:00",
    refundedAt: "2026-02-19T15:40:00+08:00",
    createdAt: "2026-02-18T10:14:00+08:00",
  },
  {
    subscriptionId: 887,
    userId: 895,
    username: "梁思妍",
    planName: "写作批改包",
    pricingName: "QUARTERLY 90天",
    orderAmount: 65900,
    settlementAmount: 41900,
    commissionAmount: 24000,
    paymentStatus: "PAID",
    commissionStatus: "SETTLED",
    paymentTime: "2026-02-15T13:44:00+08:00",
    refundedAt: null,
    createdAt: "2026-02-15T13:44:00+08:00",
  },
  {
    subscriptionId: 886,
    userId: 894,
    username: "宋亦辰",
    planName: "IELTS 高阶包",
    pricingName: "MONTHLY 30天",
    orderAmount: 48900,
    settlementAmount: 29900,
    commissionAmount: 19000,
    paymentStatus: "PAID",
    commissionStatus: "PENDING",
    paymentTime: "2026-02-12T09:05:00+08:00",
    refundedAt: null,
    createdAt: "2026-02-12T09:05:00+08:00",
  },
  {
    subscriptionId: 885,
    userId: 893,
    username: "Zoey Fang",
    planName: "口语私教包",
    pricingName: "ANNUAL 365天",
    orderAmount: 289900,
    settlementAmount: 199900,
    commissionAmount: 90000,
    paymentStatus: "PAID",
    commissionStatus: "SETTLED",
    paymentTime: "2026-02-08T17:22:00+08:00",
    refundedAt: null,
    createdAt: "2026-02-08T17:22:00+08:00",
  },
  {
    subscriptionId: 884,
    userId: 892,
    username: "韩若彤",
    planName: "IELTS 冲刺包",
    pricingName: "MONTHLY 30天",
    orderAmount: 36900,
    settlementAmount: 22900,
    commissionAmount: 14000,
    paymentStatus: "PAID",
    commissionStatus: "SETTLED",
    paymentTime: "2026-02-05T11:28:00+08:00",
    refundedAt: null,
    createdAt: "2026-02-05T11:28:00+08:00",
  },
  {
    subscriptionId: 883,
    userId: 891,
    username: "谢承宇",
    planName: "写作批改包",
    pricingName: "MONTHLY 30天",
    orderAmount: 28900,
    settlementAmount: 16900,
    commissionAmount: 12000,
    paymentStatus: "PAID",
    commissionStatus: "PENDING",
    paymentTime: "2026-02-03T19:40:00+08:00",
    refundedAt: null,
    createdAt: "2026-02-03T19:40:00+08:00",
  },
];

/**
 * 演示教师列表（从用户中筛选 role === 3）
 */
export const demoTeachers: TenantUser[] = demoUsers.filter((user) => user.role === 3);

/**
 * 演示团队成员列表
 */
export const demoTeamMembers: TeamMember[] = [
  {
    id: 1,
    tenantId: 2,
    loginName: "demo_owner",
    displayName: "演示管理员",
    role: "owner",
    status: "active",
    lastLoginAt: "2026-03-13 10:00:00",
    createdAt: "2026-01-01 00:00:00",
    updatedAt: "2026-03-13 10:00:00",
  },
  {
    id: 2,
    tenantId: 2,
    loginName: "demo_operator_1",
    displayName: "演示运营专员A",
    role: "operator",
    status: "active",
    lastLoginAt: "2026-03-12 16:30:00",
    createdAt: "2026-02-01 00:00:00",
    updatedAt: "2026-03-12 16:30:00",
  },
  {
    id: 3,
    tenantId: 2,
    loginName: "demo_operator_2",
    displayName: "演示运营专员B",
    role: "operator",
    status: "active",
    lastLoginAt: "2026-03-11 09:15:00",
    createdAt: "2026-02-15 00:00:00",
    updatedAt: "2026-03-11 09:15:00",
  },
];

/**
 * 演示操作日志列表
 */
export const demoAuditLogs: AuditLogItem[] = [
  {
    id: 1,
    tenantId: 2,
    operatorId: 1,
    operatorName: "演示管理员",
    action: "UPDATE_SETTINGS",
    actionDisplay: "更新租户配置",
    details: { field: "logo_light_url", oldValue: "https://old-logo.png", newValue: "https://new-logo.png" },
    createdAt: "2026-03-13 10:30:00",
  },
  {
    id: 2,
    tenantId: 2,
    operatorId: 1,
    operatorName: "演示管理员",
    action: "UPDATE_SETTINGS",
    actionDisplay: "更新租户配置",
    details: { field: "name", oldValue: "旧租户名", newValue: "MAGLY IELTS Demo" },
    createdAt: "2026-03-12 15:20:00",
  },
  {
    id: 3,
    tenantId: 2,
    operatorId: 2,
    operatorName: "演示运营专员A",
    action: "ASSIGN_TEACHER",
    actionDisplay: "分配教师",
    details: { userId: 1001, userName: "张雨婷" },
    createdAt: "2026-03-11 14:10:00",
  },
  {
    id: 4,
    tenantId: 2,
    operatorId: 1,
    operatorName: "演示管理员",
    action: "CREATE_TEAM_MEMBER",
    actionDisplay: "创建团队成员",
    details: { loginName: "demo_operator_2", displayName: "演示运营专员B" },
    createdAt: "2026-02-15 09:00:00",
  },
  {
    id: 5,
    tenantId: 2,
    operatorId: 1,
    operatorName: "演示管理员",
    action: "LOGIN",
    actionDisplay: "登录",
    details: {},
    createdAt: "2026-03-13 10:00:00",
  },
];

/**
 * 演示数据概览统计
 */
export const demoDashboardStats: DashboardStats = {
  totalUsers: demoUsers.length,
  newUsersThisMonth: demoUsers.filter((u) => u.boundAt.startsWith("2026-03")).length,
  activeSubscribedUsers: demoUsers.filter((u) => u.activeSubscription?.status === "ACTIVE").length,
  revenueThisMonth: demoUsers
    .filter((u) => u.activeSubscription?.status === "ACTIVE" && u.boundAt.startsWith("2026-03"))
    .reduce((sum, u) => sum + (u.activeSubscription?.actualAmount || 0), 0),
};

/**
 * 演示用户增长趋势（30天）
 */
export const demoUserGrowth30d: DashboardUserGrowthItem[] = [
  { date: "2026-02-12", count: 0 },
  { date: "2026-02-13", count: 0 },
  { date: "2026-02-14", count: 1 },
  { date: "2026-02-15", count: 1 },
  { date: "2026-02-16", count: 0 },
  { date: "2026-02-17", count: 1 },
  { date: "2026-02-18", count: 1 },
  { date: "2026-02-19", count: 1 },
  { date: "2026-02-20", count: 2 },
  { date: "2026-02-21", count: 0 },
  { date: "2026-02-22", count: 1 },
  { date: "2026-02-23", count: 1 },
  { date: "2026-02-24", count: 0 },
  { date: "2026-02-25", count: 1 },
  { date: "2026-02-26", count: 1 },
  { date: "2026-02-27", count: 0 },
  { date: "2026-02-28", count: 1 },
  { date: "2026-03-01", count: 1 },
  { date: "2026-03-02", count: 1 },
  { date: "2026-03-03", count: 1 },
  { date: "2026-03-04", count: 1 },
  { date: "2026-03-05", count: 1 },
  { date: "2026-03-06", count: 1 },
  { date: "2026-03-07", count: 1 },
  { date: "2026-03-08", count: 1 },
  { date: "2026-03-09", count: 1 },
  { date: "2026-03-10", count: 1 },
  { date: "2026-03-11", count: 1 },
  { date: "2026-03-12", count: 1 },
  { date: "2026-03-13", count: 0 },
];

/**
 * 演示收入趋势（6个月）
 */
export const demoRevenue6m: DashboardRevenueItem[] = [
  { month: "2025-10", revenue: 1200000 },
  { month: "2025-11", revenue: 1580000 },
  { month: "2025-12", revenue: 1950000 },
  { month: "2026-01", revenue: 2380000 },
  { month: "2026-02", revenue: 2890000 },
  { month: "2026-03", revenue: 3558400 },
];
